import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FlowCase } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

/**
 * "Cases by escalation level" — derived client-side from the officer/
 * supervisor inbox (GET /api/v1/flow/inbox), the only case-listing surface a
 * staff bearer token can reach: c5's GET /cases (list) is applicant-scoped
 * for non-service callers, so there is no separate cross-case admin listing
 * endpoint to aggregate over. See the build report for detail.
 */
export function SlaDashboard() {
  const { persona } = useAuth();
  const canSee = (persona?.roles.includes('CASE_OFFICER') || persona?.roles.includes('CASE_SUPERVISOR')) ?? false;
  const listQ = useApiQuery<{ items: FlowCase[] }>(canSee ? '/api/v1/flow/inbox?limit=100' : null);

  const buckets = useMemo(() => {
    const items = listQ.data?.items ?? [];
    return {
      onTime: items.filter((c) => c.escalationLevel === 0),
      level1: items.filter((c) => c.escalationLevel === 1),
      level2Plus: items.filter((c) => c.escalationLevel >= 2),
    };
  }, [listQ.data]);

  if (!canSee) {
    return (
      <div>
        <PageHeader title="SLA dashboard" subtitle="Cases grouped by escalation level" />
        <EmptyState title="No case-officer access" hint="This dashboard needs the CASE_OFFICER or CASE_SUPERVISOR role." />
      </div>
    );
  }

  const escalated = [...buckets.level2Plus, ...buckets.level1];

  return (
    <div>
      <PageHeader
        title="SLA dashboard"
        subtitle="Your visible caseload (assigned to you, or unassigned in your role's queue), grouped by escalation level"
      />
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      {listQ.loading ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <StatCard label="On time" value={buckets.onTime.length} tone="good" />
            <StatCard label="Escalation level 1 (80% SLA)" value={buckets.level1.length} tone="warn" />
            <StatCard label="Escalation level 2+ (breach)" value={buckets.level2Plus.length} tone="bad" />
          </div>

          {escalated.length === 0 ? (
            <EmptyState title="Nothing escalated" hint="Every case in your queue is currently within its SLA window." />
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Reference</th>
                    <th className="table-head">Flow</th>
                    <th className="table-head">State</th>
                    <th className="table-head">Escalation</th>
                    <th className="table-head">SLA due</th>
                  </tr>
                </thead>
                <tbody>
                  {escalated.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">
                        <Link to={`/cases/${c.refNo}`} className="text-brand-600 underline">
                          {c.refNo}
                        </Link>
                      </td>
                      <td className="table-cell text-xs">{c.flowCode}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.state} />
                      </td>
                      <td className="table-cell text-xs font-semibold">{c.escalationLevel >= 2 ? 'Breach' : 'Warning'}</td>
                      <td className="table-cell text-slate-500">{c.slaDueAt ? formatDateTime(c.slaDueAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
